import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { FilterContentComponent } from '@app/library/panes/filter-content/filter-content.component';
import { AbstractContentPane } from '@app/library/panes/models/abstract-content-pane';
import { SchoolFormModule } from '@app/school-settings/school-form/school-form.module';
import { ChapterComponent } from '@core/components/chapter/chapter.component';
import { MobileModalModule } from '@core/components/mobile-modal/mobile-modal.module';
import { ModalModule } from '@core/components/modal/modal.module';
import { NoContentStubComponent } from '@core/components/no-content-stub/no-content-stub.component';
import { SpinnersModule } from '@core/components/spinners/spinners.module';
import { AccountService, AuthService, CrispChatService } from '@core/services';
import { WebsocketService } from '@core/websocket';
import { IEntityUpdate } from '@models/lingo2-models/messaging';

import { IconComponent } from '@shared/icon/icon.component';
import { getMe } from '@store/reducers/profile.reducer';
import { addDays } from 'date-fns';
import { WebEvent } from 'lingo2-api-models';
import { CSchool, EContentOrder, Meeting, User, UserRoleEnum, UserStatusEnum } from 'lingo2-models';
import { Router as ContentRouter } from 'lingo2-models/dist/content/router';
import { FindSchoolsStrategyEnum } from 'lingo2-models/dist/school/interfaces/find-school-filter';
import { InViewportModule } from 'ng-in-viewport';
import { OnUiButtonModule } from 'onclass-ui';
import { debounceTime, filter, takeUntil } from 'rxjs';
import { SchoolMiniComponent } from './school-mini/school-mini.component';

@Component({
  selector: 'app-my-classes-schools',
  standalone: true,
  imports: [
    LocaleSharedModule,
    CommonModule,
    SpinnersModule,
    RouterModule,

    OnUiButtonModule,
    ModalModule,
    SchoolMiniComponent,
    MobileModalModule,
    NoContentStubComponent,
    SchoolFormModule,
    InViewportModule,
    FilterContentComponent,
    ChapterComponent,
    IconComponent,
  ],
  templateUrl: './my-classes-schools.component.html',
  styleUrls: ['./my-classes-schools.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyClassesSchoolsComponent extends AbstractContentPane implements OnInit, OnDestroy {
  private websocketService = inject(WebsocketService);
  private authService = inject(AuthService);
  private crispChat = inject(CrispChatService);
  protected router = inject(Router);

  @ViewChild('empty', { read: ViewContainerRef }) private emptyContainerRef: ViewContainerRef;
  public schoolRoute = ContentRouter.schoolRoute;
  public categoryTabs = [
    { id: 1, code: 'owned', title: 'my-classes.no-schools.filter.owner', count: 1 },
    { id: 2, code: 'teacher', title: 'my-classes.no-schools.filter.teacher', count: 1 },
    { id: 3, code: 'student', title: 'my-classes.no-schools.filter.student', count: 1 },
  ];
  public selectedTab: any = { id: 1, code: 'owner', title: 'owner' };
  public newSchoolOpenForm = false;
  public currentDayOfWeek: Date = new Date();
  public _schools: CSchool[];
  public schoolsLoaded = false;
  public contentLoaded = true;
  public schoolsTotalPages = 1;
  public get me(): User {
    return this._me;
  }
  public isGuest: boolean;

  private _me: User;
  private meId: string;

  ngOnInit() {
    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => {
          this._me = me;
          this.isGuest = me?.status === UserStatusEnum.guest;
          this.onSelectItem(this.selectedTab);
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });

    this.initScheduleRefresh();
    this.loadSchools();
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
  }

  protected loadSchools() {
    if (this._pane.loading) {
      return;
    }
    this._pane.loaded = false;
    this._pane.schoolsFilter.order = EContentOrder.date_desc;
    this.loadPane();
    super.loadSchools();
    /** @see onContentLoaded() */
  }

  protected onContentLoaded() {
    this.isVisible = true;
    this.contentLoaded = true;
  }

  hasContent(): boolean {
    return true;
  }

  public onToggleSchoolForm(e?: boolean) {
    if (this.isDevTester()) {
      this.newSchoolOpenForm = !this.newSchoolOpenForm;
      if (e) {
        this.onSelectItem(this.categoryTabs[0], true);
      }
    } else {
      this.crispChat.openChat();
    }
  }

  public isDevTester() {
    return this._me.roles.includes(UserRoleEnum.developer) || this._me.roles.includes(UserRoleEnum.tester);
  }

  public dateWithoutTime(date: Date): Date {
    const day = new Date(date);
    day.setHours(0, 0, 0, 0);
    return addDays(day, 0);
  }

  public minutes(date): number {
    return date.getHours() * 60 + date.getMinutes();
  }

  private goToSchool(school: CSchool) {
    void this.router.navigate(this.schoolRoute(school)).then();
  }

  public onSelectItem(item, force = false): void {
    if (this.selectedTab.id === item.id && !force) {
      return;
    }
    this._pane = {
      ...this._pane,
      loaded: false,
      schoolsFilter: {
        ...this._pane.schoolsFilter,
        use: FindSchoolsStrategyEnum[item.code],
      },
      schools: [],
    };
    this.selectedTab = item;
    this.loadSchools();
  }

  public get filteredSchools() {
    if (this.pane.display_mode !== 'row') {
      return (this._pane.contentItems || []).slice(0, 4);
    }
    return this._pane.contentItems || [];
  }

  public trackMeetingByFn(index, item: Meeting): string {
    return item.id;
  }

  public get isAsIfTeacher(): boolean {
    return AccountService.isAsIfTeacher(this._me);
  }

  protected initScheduleRefresh() {
    /** уведомление об изменении расписания пользователя */
    this.websocketService
      .on<IEntityUpdate>(WebEvent.Update)
      .pipe(
        filter((update) => update.entity === 'schedule' && update.id === this.meId),
        debounceTime(1000),
      )
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.loadSchools();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });

    /** Реконнект к веб-сокетам */
    this.websocketService.status$
      .pipe(debounceTime(1000))
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (isOnline) => {
          if (isOnline) {
            this.watchSchoolRefresh(this.meId);
            this.loadSchools();
          }
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.watchSchoolRefresh(this.meId);
          this.loadSchools();
        },
      });

    /** Смена пользователя */
    this.authService.user$
      .pipe(filter((me) => !!me && me.id !== this.meId))
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => {
          this.meId = me.id;
          this.watchSchoolRefresh(me.id, this.meId);
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });

    this.loadSchools();
  }

  /** Настроить обновление расписания по сообщениям из веб-сокетов */
  private watchSchoolRefresh(me_id: string, old_me_id?: string) {
    if (old_me_id) {
      this.websocketService.stopWatching('school', old_me_id);
    }
    if (me_id) {
      this.websocketService.startWatching('school', me_id);
    }
  }

  public onInViewportBottomChange(reached) {
    if (reached.visible) {
      if (this.pane.pagination.page < this.schoolsTotalPages) {
        this.loadNextPage();
      }
    }
  }
}
